/**
 * Helpers for telling "the backend is down" apart from "you are not signed in".
 *
 * During a rolling deploy the gateway answers 502/503/504 (or the request fails
 * outright). Those responses say nothing about the user's session, so the app
 * must not treat them as a logout or fall back to a password login form —
 * it shows a blank page with a "service temporarily unavailable" toast instead.
 */

/** 0 is what the API layer uses for network failures (see services/api/core.ts). */
const SERVICE_UNAVAILABLE_STATUSES = new Set([0, 500, 502, 503, 504])

/** True for gateway/server statuses that mean the backend is unreachable. */
export function isServiceUnavailableStatus(status: number): boolean {
  return SERVICE_UNAVAILABLE_STATUSES.has(status)
}

/**
 * True when an error means the backend could not be reached / did not respond,
 * rather than a real answer from it (401, 403, 404, 422, …).
 *
 * Duck-types `status` instead of importing ApiError so this stays free of any
 * dependency on the API layer.
 */
export function isServiceUnavailableError(err: unknown): boolean {
  const status = (err as { status?: unknown } | null)?.status
  if (typeof status === 'number') {
    return isServiceUnavailableStatus(status)
  }

  if (err instanceof Error) {
    // Raw fetch rejections and the auth-check timeout race in AuthContext.
    return /failed to fetch|networkerror|network request failed|load failed|timeout|aborted/i.test(
      err.message
    )
  }

  return false
}

/** Copy shown to users while the platform is unreachable. */
export const SERVICE_UNAVAILABLE_MESSAGE =
  'Service is temporarily unavailable. Please try again later.'
